import type { HubspotAssociationLabel } from '@/types/vacancy';

export const candidateTags = [
  {
    id: 'executive-talent',
    label: 'Executive Talent',
    description: 'Kandidaten met executive ervaring',
  },
  {
    id: 'fast-track',
    label: 'Fast Track',
    description: 'Veelbelovende kandidaten voor snelle opvolging',
  },
  {
    id: 'international',
    label: 'International',
    description: 'Beschikbaar voor internationale opdrachten',
  },
] as const;

export type CandidateTagId = (typeof candidateTags)[number]['id'];

export type CandidateTag = {
  id: CandidateTagId;
  label: string;
  description?: string;
};

export type CandidateStageCategory =
  | 'pipeline'
  | 'interview'
  | 'aangenomen'
  | 'afgewezen';

export const candidateStages = [
  {
    id: 'sollicitanten',
    label: 'Sollicitanten',
    color: '#38BDF8',
    indicatorClass: 'bg-sky-400',
    category: 'pipeline',
    hubspotLabel: 'Gesolliciteerd',
  },
  {
    id: 'uitnodigingen',
    label: 'Uitnodigingen',
    color: '#22C55E',
    indicatorClass: 'bg-green-500',
    category: 'pipeline',
    hubspotLabel: 'Uitnodigen',
  },
  {
    id: 'gesprek-sw',
    label: 'Gesprek S&W',
    color: '#FACC15',
    indicatorClass: 'bg-yellow-300',
    category: 'interview',
    hubspotLabel: 'Gesprek s&w',
  },
  {
    id: 'gesprek-opdrachtgever',
    label: 'Gesprek Opdrachtgever',
    color: '#F97316',
    indicatorClass: 'bg-orange-500',
    category: 'interview',
    hubspotLabel: 'Eerste gesprek opdrachtgever',
  },
  {
    id: 'assessment',
    label: 'Assessment',
    color: '#A855F7',
    indicatorClass: 'bg-purple-500',
    category: 'pipeline',
    hubspotLabel: 'Assessment',
  },
  {
    id: 'aangenomen',
    label: 'Aangenomen',
    color: '#10B981',
    indicatorClass: 'bg-emerald-500',
    category: 'aangenomen',
    hubspotLabel: 'Aangenomen',
  },
  {
    id: 'afgewezen',
    label: 'Afgewezen',
    color: '#F87171',
    indicatorClass: 'bg-red-400',
    category: 'afgewezen',
    hubspotLabel: 'Afgewezen',
  },
  {
    id: 'teruggetrokken',
    label: 'Teruggetrokken',
    color: '#364153',
    indicatorClass: 'bg-gray-700',
    category: 'afgewezen',
    hubspotLabel: null,
  }
] as const;

export type CandidateStage = (typeof candidateStages)[number];
export type CandidateStatusId = CandidateStage['id'];
export type CandidateStagesById = ReadonlyMap<CandidateStatusId, CandidateStage>;

export type CandidateStatus = {
  id: CandidateStatusId;
  label: string;
};

export const candidateStatuses: CandidateStatus[] = candidateStages.map(
  ({ id, label }) => ({ id, label })
);

export const candidateStagesById: CandidateStagesById = new Map(
  candidateStages.map((stage) => [stage.id, stage])
);

const resolveEnvFlag = (): string | undefined => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const value = import.meta.env.VITE_USE_LEGACY_ASSOCIATIONS;
    if (typeof value === 'string') {
      return value;
    }
  }
  return undefined;
};

const useLegacyAssociations =
  (resolveEnvFlag() ?? '').toLowerCase() === 'true';

const stageFor = (id: CandidateStatusId): CandidateStage => {
  const stage = candidateStagesById.get(id);
  if (!stage) {
    throw new Error(`Unknown candidate stage "${id}"`);
  }
  return stage;
};

const legacyHubspotTypeIdEntries: Array<[string, CandidateStage]> = [
  ['79', stageFor('sollicitanten')],
  ['85', stageFor('uitnodigingen')],
  ['83', stageFor('gesprek-sw')],
  ['103', stageFor('gesprek-opdrachtgever')],
  ['101', stageFor('assessment')],
  ['81', stageFor('aangenomen')],
  ['23', stageFor('afgewezen')],
  ['97', stageFor('teruggetrokken')],
];

const newHubspotTypeIdEntries: Array<[string, CandidateStage]> = [
  ['145', stageFor('sollicitanten')],
  ['95', stageFor('sollicitanten')],
  ['105', stageFor('sollicitanten')],
  ['93', stageFor('sollicitanten')],
  ['67', stageFor('uitnodigingen')],
  ['65', stageFor('uitnodigingen')],
  ['151', stageFor('afgewezen')],
  ['85', stageFor('afgewezen')],
  ['115', stageFor('afgewezen')],
  ['111', stageFor('afgewezen')],
  ['113', stageFor('afgewezen')],
  ['148', stageFor('afgewezen')],
  ['150', stageFor('afgewezen')],
  ['107', stageFor('afgewezen')],
  ['99', stageFor('afgewezen')],
  ['87', stageFor('afgewezen')],
  ['23', stageFor('afgewezen')],
  ['91', stageFor('afgewezen')],
  ['69', stageFor('gesprek-opdrachtgever')],
  ['73', stageFor('gesprek-opdrachtgever')],
  ['75', stageFor('gesprek-opdrachtgever')],
  ['77', stageFor('gesprek-opdrachtgever')],
  ['103', stageFor('gesprek-opdrachtgever')],
  ['109', stageFor('gesprek-opdrachtgever')],
  ['71', stageFor('gesprek-sw')],
  ['79', stageFor('assessment')],
  ['101', stageFor('assessment')],
  ['122', stageFor('assessment')],
  ['81', stageFor('aangenomen')],
  ['27', stageFor('aangenomen')],
  ['97', stageFor('teruggetrokken')],
  ['89', stageFor('teruggetrokken')],
];

const hubspotTypeIdMap = new Map<string, CandidateStage>(
  useLegacyAssociations ? legacyHubspotTypeIdEntries : newHubspotTypeIdEntries
);

const hubspotLabelCandidates: Record<CandidateStatusId, string[]> = {
  sollicitanten: ['Gesolliciteerd', 'Sollicitanten', 'Applied', 'Reserve kandidaat'],
  uitnodigingen: ['Uitnodigen', 'Uitnodiging', 'Benaderd'],
  'gesprek-sw': ['Gesprek s&w', 'Gesprek S&W', 'Gesprek sw'],
  'gesprek-opdrachtgever': [
    'Eerste gesprek opdrachtgever',
    'Gesprek opdrachtgever',
    'Tweede gesprek opdrachtgever',
    'Bespreken opdrachtgever',
    'Derde gesprek opdrachtgever',
  ],
  assessment: [
    'Assessment',
    'Arbeidvoordwaardengesprek',
    'Arbeidsvoorwaarden gesprek',
    'Arbeidsvoorwaardengesprek',
  ],
  aangenomen: ['Aangenomen', 'Accepted'],
  afgewezen: [
    'Afgewezen',
    'Reject',
    'Afgewezen op CV',
    'Afgewezen na gesprek S&W',
    'Afgewezen op arbeidsvoorwaarden',
    'Afgewezen door opdrachtgever',
    'Nog afwijzen door office',
    'Uitgebreid afwijzen op CV',
  ],
  teruggetrokken: ['Kandidaat teruggetrokken', 'Withdrawn'],
};

const hubspotSlugCandidates: Record<CandidateStatusId, string[]> = {
  sollicitanten: ['gesolliciteerd', 'sollicitanten', 'applied', 'reserve-kandidaat'],
  uitnodigingen: ['uitnodigingen', 'uitnodigen', 'benaderd'],
  'gesprek-sw': ['gesprek-s-w', 'gesprek-sw'],
  'gesprek-opdrachtgever': [
    'eerste-gesprek-opdrachtgever',
    'gesprek-opdrachtgever',
    'tweede-gesprek-opdrachtgever',
    'derde-gesprek-opdrachtgever',
    'bespreken-opdrachtgever',
  ],
  assessment: ['assessment', 'arbeidsvoorwaardengesprek'],
  aangenomen: ['aangenomen', 'accepted'],
  afgewezen: [
    'reject',
    'afgewezen-op-cv',
    'afgewezen-door-opdrachtgever',
    'afgewezen-op-arbeidsvoorwaarden',
    'uitgebreid-afwijzen-op-cv',
    'nog-afwijzen-door-office',
  ],
  teruggetrokken: ['teruggetrokken', 'kandidaat-teruggetrokken', 'withdrawn'],
};

export const getCandidateStage = (id: CandidateStatusId) =>
  candidateStagesById.get(id);

export const getCandidateStageCategory = (
  id: CandidateStatusId
): CandidateStageCategory =>
  candidateStagesById.get(id)?.category ?? 'pipeline';

export const getHubspotStageLabel = (id: CandidateStatusId): string | null => {
  const stage = candidateStagesById.get(id);
  if (!stage) {
    return null;
  }
  return stage.hubspotLabel;
};

export const getHubspotStageLabelCandidates = (
  id: CandidateStatusId
): string[] => {
  const stage = candidateStagesById.get(id);
  if (!stage) {
    return [];
  }
  const base = stage.hubspotLabel ? [stage.hubspotLabel] : [];
  const extras = hubspotLabelCandidates[id] ?? [];
  const combined = [...base, ...extras];
  const unique = new Set<string>();
  combined.forEach((label) => {
    const trimmed = label.trim();
    if (trimmed) {
      unique.add(trimmed);
    }
  });
  return Array.from(unique);
};

export const getStageByAssociationTypeId = (typeId?: string | null): CandidateStage | undefined => {
  if (!typeId) {
    return undefined;
  }
  return hubspotTypeIdMap.get(typeId);
};

const slugify = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const getHubspotStageSlugCandidates = (
  id: CandidateStatusId
): string[] => {
  const stage = candidateStagesById.get(id);
  const base = stage?.hubspotLabel ? [slugify(stage.hubspotLabel)] : [];
  const extras = hubspotSlugCandidates[id] ?? [];
  const combined = [...base, ...extras];
  const unique = new Set<string>();
  combined.forEach((slug) => {
    const normalized = slug.trim().toLowerCase();
    if (normalized) {
      unique.add(normalized);
    }
  });
  return Array.from(unique);
};

export type StageAssociation = {
  stageId: CandidateStatusId;
  typeId: string;
  label: string;
  slug: string;
};

export type StageAssociationLookup = Partial<Record<CandidateStatusId, StageAssociation>>;

const PREFERRED_ASSOCIATION_TYPE_IDS: Partial<Record<CandidateStatusId, string[]>> = {
  afgewezen: ['151', '85', '23'],
};

export const buildStageAssociationLookup = (
  labels: HubspotAssociationLabel[]
): StageAssociationLookup => {
  const lookup: StageAssociationLookup = {};
  const bySlug = new Map<string, HubspotAssociationLabel>();
  const byTypeId = new Map<string, HubspotAssociationLabel>();

  labels.forEach((label) => {
    bySlug.set(label.slug.toLowerCase(), label);
    bySlug.set(slugify(label.label), label);
    byTypeId.set(label.typeId, label);
  });

  candidateStages.forEach((stage) => {
    const preferredTypeIds = PREFERRED_ASSOCIATION_TYPE_IDS[stage.id];
    if (preferredTypeIds && preferredTypeIds.length > 0) {
      for (const typeId of preferredTypeIds) {
        const match = byTypeId.get(typeId);
        if (match) {
          lookup[stage.id] = {
            stageId: stage.id,
            typeId: match.typeId,
            label: match.label,
            slug: match.slug,
          };
          return;
        }
      }
    }
    const slugCandidates = getHubspotStageSlugCandidates(stage.id);
    for (const candidateSlug of slugCandidates) {
      const match = bySlug.get(candidateSlug);
      if (match) {
        lookup[stage.id] = {
          stageId: stage.id,
          typeId: match.typeId,
          label: match.label,
          slug: match.slug,
        };
        return;
      }
    }
    if (slugCandidates.length > 0) {
      const fallbackSlug = slugify(stage.label);
      const fallbackMatch = bySlug.get(fallbackSlug);
      if (fallbackMatch) {
        lookup[stage.id] = {
          stageId: stage.id,
          typeId: fallbackMatch.typeId,
          label: fallbackMatch.label,
          slug: fallbackMatch.slug,
        };
        return;
      }
    }
    if (import.meta.env?.DEV) {
      console.warn(
        `[associations] no HubSpot association label found for stage "${stage.id}"`,
        {
          expectedSlugs: slugCandidates,
        }
      );
    }
  });

  return lookup;
};

const stageLabelOverridesBySlug: Partial<Record<CandidateStatusId, Record<string, string>>> = {
  afgewezen: {
    'afgewezen-op-cv': 'Uitgebreid afwijzen op CV',
  },
};

const stageLabelOverridesByValue: Partial<Record<CandidateStatusId, Record<string, string>>> = {
  afgewezen: {
    'afgewezen op cv': 'Uitgebreid afwijzen op CV',
  },
};

const normalizeRawText = (value?: string | null) => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const normalizeStageDesiredLabel = (
  stageId: CandidateStatusId,
  label?: string | null,
  slug?: string | null
): string | null => {
  const slugKey = normalizeRawText(slug)?.toLowerCase();
  if (slugKey) {
    const override = stageLabelOverridesBySlug[stageId]?.[slugKey];
    if (override) {
      return override;
    }
  }
  const normalizedLabel = normalizeRawText(label);
  if (!normalizedLabel) {
    return null;
  }
  const valueOverride =
    stageLabelOverridesByValue[stageId]?.[normalizedLabel.toLowerCase()];
  if (valueOverride) {
    return valueOverride;
  }
  return normalizedLabel;
};

export type CandidateDateRange = {
  from: Date | null;
  to: Date | null;
};

export type CandidateFilters = {
  tags: CandidateTagId[];
  status: CandidateStatusId | null;
  dateRange: CandidateDateRange;
};

export const createDefaultCandidateFilters = (): CandidateFilters => ({
  tags: [],
  status: null,
  dateRange: {
    from: null,
    to: null,
  },
});
